import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { OptionalFieldsComponent } from 'projects/ui-library/src/lib/components/optional-fields/optional-fields.component';
import { SearchSelectComponent } from 'projects/ui-library/src/lib/components/search-select/search-select.component';
import { Label } from 'src/app/interfaces/api/label';
import { OptionalField } from 'src/app/interfaces/api/imageStatus';
import { LabelProject } from 'src/app/interfaces/api/labelProject';

/** Lightweight view of the annotated risk box the popup edits. */
export interface PopupTag {
  label?: string;
  author?: string;
  color?: string;
  probability?: number;
  optionalFields?: OptionalField[];
}

/**
 * Per-annotation popup ported from unifield-label (gallery annotation-popup): anchored to its
 * box by the labeler through a CDK overlay. Shows the label header (name, author, delete/close)
 * and the configured optional fields. Manual annotations only (the labeler draws confirmed boxes).
 */
@Component({
  selector: 'app-annotation-popup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    TranslateModule,
    OptionalFieldsComponent,
    SearchSelectComponent
  ],
  templateUrl: './annotation-popup.component.html',
  styleUrls: ['./annotation-popup.component.scss']
})
export class AnnotationPopupComponent {
  @Input() tag!: PopupTag;
  @Input() labelProject!: LabelProject;
  @Input() labelCatalogEntry: Label | null = null;
  @Input() displayTitle: string = '';
  /** Where the arrow sits relative to the box: box-side popup. */
  @Input() arrowSide: 'left' | 'right' | 'top' | 'bottom' = 'left';
  /** The author line is hidden for the current user's own annotations. */
  @Input() currentUserEmail: string = '';
  @Input() hideArrow: boolean = false;
  /** Mobile: render as a right-side slide-in drawer instead of a box-anchored popup. */
  @Input() mobile: boolean = false;
  @Input() disabled: boolean = false;
  @Input() labelReadOnly: boolean = false;
  @Input() comment: string = '';
  @Input() labels: Label[] = [];
  @Input() selectedLabel: Label | undefined;
  /** AI prediction: shows its percentage and an edit action that turns it into a user tag. */
  @Input() probability?: number;
  /** Hides the label picker, optional fields and comment (AI predictions, non-risk tags). */
  @Input() hideFields: boolean = false;

  @Output() closed = new EventEmitter<void>();
  @Output() deleteTag = new EventEmitter<void>();
  @Output() editTag = new EventEmitter<void>();
  @Output() fieldsChange = new EventEmitter<OptionalField[]>();
  @Output() commentChange = new EventEmitter<string>();
  @Output() labelChanged = new EventEmitter<string>();
  @Output() hovered = new EventEmitter<boolean>();

  private clonedOptions: any[] | null = null;

  /**
   * Per-popup CLONE of the module field config. OptionalFieldsComponent mutates its option
   * objects (option.value), so sharing the config would leak values across popups. Single-select
   * fields with 2–5 options render as toggle buttons (e.g. Criticité).
   */
  fieldOptions(): any[] {
    if (!this.clonedOptions && this.labelProject?.optionalFields) {
      this.clonedOptions = structuredClone(this.labelProject.optionalFields as any[])
        // Only criticity + description in the popup; the comment is the dedicated textarea below.
        .filter((option: any) => {
          const key = (option.label || '').toLowerCase();
          return key === 'criticity' || key === 'description' || option.type === 'dynamic';
        })
        .map((option: any) => {
        // The "description" field lists the label's suggested values → shown as "Propositions".
        if ((option.label || '').toLowerCase() === 'description') option.displayLabel = 'Propositions';
        const single = !['dynamic', 'checkbox', 'textarea', 'number'].includes(option.type || 'text') && !option.multiple;
        if (single && Array.isArray(option.options) && option.options.length >= 2 && option.options.length <= 5) {
          option.type = 'buttons';
        }
        return option;
      });
    }
    return this.clonedOptions ?? [];
  }

  private editedTag?: PopupTag;
  private editedFields: OptionalField[] = [];

  hasMultiValue(): boolean {
    const fields = this.editedTag === this.tag ? this.editedFields : this.tag?.optionalFields || [];
    return fields.some(f => f.key.toLowerCase() === 'description' && f.value.filter(v => !!v).length > 1);
  }

  missingRequired(): boolean {
    const fields = this.tag?.optionalFields || [];
    return this.fieldOptions().some((opt: any) => {
      const required = opt.type === 'dynamic'
        ? ((this.labelCatalogEntry?.optionalFields?.length ?? 0) > 0)
        : (!!opt.required && (!Array.isArray(opt.options) || opt.options.length > 0));
      if (!required) return false;
      const f = fields.find(x => x.key === opt.label);
      return !f || !f.value?.length || !f.value.some(v => !!v);
    });
  }

  attemptClose(): void {
    // Close always dismisses; required-field enforcement happens at image save, not popup close.
    this.closed.emit();
  }

  onDelete(): void {
    this.deleteTag.emit();
  }

  onFieldsChange(fields: OptionalField[]): void {
    const filtered = (fields || []).filter(field => field.value?.length && field.value.some(v => !!v));
    this.editedTag = this.tag;
    this.editedFields = filtered;
    this.fieldsChange.emit(filtered);
  }
}
